<template>
  <div ref="dragLayoutRef" :class="[bem.b()]">
    <!-- 左侧 -->
    <div
      ref="leftPanelRef"
      :class="[
        bem.e('left-panel'),
        showDragBtn ? bem.is('drag-btn') : '',
        speed ? bem.is(speed) : '',
      ]"
      :style="{
        width: props.flex && !foldState ? 'auto' : `${leftWidth}px`,
      }"
    >
      <slot name="left-layout"></slot>
      <div
        :class="[
          drag || fold ? bem.e('divider') : '',
          bem.is(drag && !(fold && foldState) ? 'drag' : 'undrag'),
        ]"
        :style="{
          backgroundColor:
            leftWidth === 0 && !props.flex
              ? 'transparent'
              : dividerColor || 'var(--color-split)',
        }"
        @mousedown.prevent="startDrag"
      >
        <ll-icon
          v-if="showDragBtn"
          :class="[bem.e('divider-icon')]"
          icon-name="icon-drag2"
          :size="8"
        />
        <!-- 折叠按钮 -->

        <div
          v-if="fold"
          ref="foldBtnRef"
          :class="[bem.e('fold-btn'), bem.is(foldState ? 'fold' : 'unfold')]"
          @mousedown.stop
          @click="handleFold(!foldState)"
        >
          <ll-tooltip
            :open="true"
            :tip="foldState ? '展开' : '收起'"
            placement="right"
            :arrow="false"
            :get-popup-container="() => foldBtnRef"
          >
            <ll-icon
              :icon-name="
                foldState ? 'icon-solid-arrow-right' : 'icon-solid-arrow-left'
              "
              :size="12"
            />
          </ll-tooltip>
        </div>
      </div>
    </div>

    <!-- 右侧 -->
    <div :class="bem.e('right-panel')">
      <slot name="right-layout"></slot>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { createNamespace } from '@ll-plus/utils'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { flexSplitLayoutProps } from './flex-split-layout'

defineOptions({ name: 'LlFlexSplitLayout' })

const bem = createNamespace('flex-split-layout')

const props = defineProps(flexSplitLayoutProps)
const emits = defineEmits([
  'update:leftCurrentWidth',
  'update:foldState',
  'handleFold',
])

const leftWidth = ref(0) // 左侧实时宽度
const isDragging = ref(false) // 是否拖拽中
const startX = ref(0)
const startWidth = ref(0)
const dragLayoutRef = ref() // 整个容器的Ref
const leftPanelRef = ref() // 左侧拖拽区域Ref

const layoutWidth = ref(0) // 整个容器区域宽度
const foldBtnRef = ref()

// 是否显示拖拽按钮
const showDragBtn = computed(() => {
  return props.drag && props.isShowBtn && !(props.fold && foldState.value)
})

// 记录折叠前的宽度，用于展开时恢复
const preFoldWidth = ref(0)

// 处理折叠状态 - 支持双向绑定或内部状态
const foldState = computed({
  get: () => internalFoldState.value,
  set: (value) => {
    internalFoldState.value = value
    emits('update:foldState', value)
    emits('handleFold', value)
  },
})

// 内部折叠状态，用于当没有双向绑定时
const internalFoldState = ref(props.foldState || false)

// 初始化宽度
const initLayoutWidth = () => {
  leftWidth.value =
    props.leftCurrentWidth > 0
      ? props.leftCurrentWidth
      : props.leftDefaultWidth > 0
        ? props.leftDefaultWidth
        : 0

  // 初始化时记录宽度
  preFoldWidth.value = leftWidth.value
}

// 宽度布局维护函数
const constrainBoundaries = () => {
  if (!dragLayoutRef.value) return

  // 对参数进行非负处理
  const leftMin = props.leftMinWidth
  const leftMax = Math.max(0, props.leftMaxWidth)
  const rightMin = Math.max(0, props.rightMinWidth)

  // 获取实时容器宽度
  layoutWidth.value = dragLayoutRef.value.clientWidth

  // 边界1：总宽度不足时
  if (layoutWidth.value < leftMin + rightMin) {
    const safeWidth = Math.max(0, layoutWidth.value - rightMin)
    leftWidth.value = Math.min(leftMax, safeWidth)
    return
  }

  // 边界2：左侧过小
  if (leftWidth.value < leftMin) {
    leftWidth.value = leftMin
  }

  // 边界3：左侧过大
  if (leftWidth.value > leftMax) {
    leftWidth.value = leftMax
  }

  // 边界4：右侧被挤压
  if (layoutWidth.value - leftWidth.value < rightMin) {
    leftWidth.value = Math.max(0, layoutWidth.value - rightMin)
  }

  // 确保左侧宽度不为负
  leftWidth.value = Math.max(0, leftWidth.value)
}

// 折叠/展开函数
const handleFold = (state?: boolean) => {
  if (!props.fold) return // 如果未启用折叠功能，直接返回

  const newState = state !== undefined ? state : !foldState.value
  if (newState) {
    // 折叠：记录当前宽度并设置为0
    preFoldWidth.value = leftWidth.value
    leftWidth.value = 0
  } else {
    // 展开：恢复到折叠前的宽度，如果没有记录则使用默认宽度
    const targetWidth =
      preFoldWidth.value > 0 ? preFoldWidth.value : props.leftDefaultWidth

    leftWidth.value = targetWidth
    // 确保宽度在合理范围内
    constrainBoundaries()
  }

  foldState.value = newState
}

// 拖拽开始
const startDrag = (e: MouseEvent) => {
  // 关闭拖拽 或者当前处于折叠状态则停止
  if (!props.drag || (foldState.value && props.fold)) return

  isDragging.value = true
  startX.value = e.clientX
  startWidth.value = leftPanelRef.value.offsetWidth

  // 获取容器当前总宽度
  layoutWidth.value = dragLayoutRef.value.clientWidth

  // 约束边界
  constrainBoundaries()

  // 添加全局事件监听
  window.addEventListener('mousemove', onDrag)
  window.addEventListener('mouseup', stopDrag)
}

// 拖拽结束
const stopDrag = () => {
  isDragging.value = false
  emits('update:leftCurrentWidth', leftWidth.value) // 抛出当前宽度
  preFoldWidth.value = leftWidth.value
  removeListeners()
}

// 拖拽逻辑
const onDrag = (e: MouseEvent) => {
  if (!isDragging.value || !leftPanelRef.value) return

  // 实时获取容器宽度（解决视口宽度变化同时拖拽的问题）
  const currentLayoutWidth = dragLayoutRef.value.clientWidth

  // 对边界参数进行非负处理
  const safeLeftMin = props.leftMinWidth
  const safeLeftMax = Math.max(0, props.leftMaxWidth)
  const safeRightMin = Math.max(0, props.rightMinWidth)

  // 动态计算最大允许宽度（考虑当前容器尺寸）
  const maxAllowedLeftWidth = Math.max(0, currentLayoutWidth - safeRightMin)

  const deltaX = e.clientX - startX.value
  let newWidth = startWidth.value + deltaX

  // 双层限制逻辑
  newWidth = Math.min(
    Math.min(safeLeftMax, maxAllowedLeftWidth),
    Math.max(safeLeftMin, newWidth),
  )

  // 确保最终值不为负
  leftWidth.value = Math.max(0, newWidth)
}

// 添加窗口监听（响应式修正）
const handleResize = () => {
  // 在窗口调整时调用边界约束
  if (props.fold && foldState.value) return
  constrainBoundaries()
}

watch(
  () => props.leftCurrentWidth,
  (val) => {
    if (!isDragging.value && !foldState.value) {
      leftWidth.value = val < 0 ? 0 : val
      preFoldWidth.value = leftWidth.value // 更新记录宽度
    }
  },
)

watch(
  () => leftWidth.value,
  (val) => {
    if (!isDragging.value) {
      emits('update:leftCurrentWidth', val)
    }
  },
)

watch(
  () => props.foldState,
  (newState) => {
    if (newState !== foldState.value) {
      handleFold(newState)
    }
  },
)

// 清理事件监听
const removeListeners = () => {
  window.removeEventListener('mousemove', onDrag)
  window.removeEventListener('mouseup', stopDrag)
}

// 组件挂载
onMounted(() => {
  window.addEventListener('resize', handleResize)
  initLayoutWidth()

  if (props.foldState) {
    handleFold(props.foldState)
    emits('update:leftCurrentWidth', leftWidth.value)
  }
})

// 组件卸载时清理
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  removeListeners()
})

defineExpose({ handleFold })
</script>
